import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { supabase } from '../supabaseClient';

const ExperimentFormModal = ({ isOpen, onClose, onSave, experiment, projectId }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'App Development',
    status: 'Success',
    hypothesis: '',
    lesson_learned: '',
    method: '',
    expected_result: '',
    actual_result: '',
    linked_test: '',
  });
  const [dumpText, setDumpText] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCreator, setIsCreator] = useState(true);
  const [accessError, setAccessError] = useState(false);

  // Update formData when experiment changes
  useEffect(() => {
    if (experiment) {
      setFormData({
        title: experiment.title || '',
        category: experiment.category || 'App Development',
        status: experiment.status || 'Success',
        hypothesis: experiment.hypothesis || '',
        lesson_learned: experiment.lesson_learned || '',
        method: experiment.method || '',
        expected_result: experiment.expected_result || '',
        actual_result: experiment.actual_result || '',
        linked_test: experiment.linked_test || '',
      });
    } else {
      setFormData({
        title: '',
        category: 'App Development',
        status: 'Success',
        hypothesis: '',
        lesson_learned: '',
        method: '',
        expected_result: '',
        actual_result: '',
        linked_test: '',
      });
      setDumpText('');
    }
  }, [experiment]);

  const handleFillByAI = async () => {
    if (!dumpText.trim()) {
      alert('Please enter some experiment information in the text box first.');
      return;
    }

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey || apiKey.trim() === '' || apiKey === 'your_actual_gemini_api_key_here') {
      alert('Gemini API key is not configured.');
      return;
    }

    setAiLoading(true);
    try {
      const { GoogleGenerativeAI } = await import('@google/generative-ai');
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

      const prompt = `You are an experiment logging assistant. Given the following raw experiment notes, extract and structure the information into a JSON object with these exact fields:
- title (string, concise experiment title)
- category (one of: "App Development", "Frontend", "Backend", "Cloud")
- status (one of: "Success", "Partial Success", "Failure", "In Progress", "Pending")
- hypothesis (string, what was expected to happen)
- method (string, how the experiment was conducted step by step)
- expected_result (string, predicted outcome)
- actual_result (string, what actually happened)
- lesson_learned (string, key takeaway)
- linked_test (string, any referenced test/case name or ID, empty string if none)

Return ONLY valid JSON with no markdown, no code blocks, no explanation. If a field cannot be determined from the notes, use an empty string for that field.

Experiment notes:
${dumpText}`;

      const result = await model.generateContent(prompt);
      const text = result.response.text().trim();

      let parsed;
      try {
        // Strip any accidental markdown fences
        const clean = text.replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
        parsed = JSON.parse(clean);
      } catch {
        alert('AI returned an unexpected response. Please try again.');
        return;
      }

      const validCategories = ['App Development', 'Frontend', 'Backend', 'Cloud'];
      const validStatuses = ['Success', 'Partial Success', 'Failure', 'In Progress', 'Pending'];

      setFormData(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        category: validCategories.includes(parsed.category) ? parsed.category : prev.category,
        status: validStatuses.includes(parsed.status) ? parsed.status : prev.status,
        hypothesis: parsed.hypothesis || prev.hypothesis,
        method: parsed.method || prev.method,
        expected_result: parsed.expected_result || prev.expected_result,
        actual_result: parsed.actual_result || prev.actual_result,
        lesson_learned: parsed.lesson_learned || prev.lesson_learned,
        linked_test: parsed.linked_test || prev.linked_test,
      }));
    } catch (error) {
      console.error('AI fill error:', error);
      alert('Error calling AI: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      if (experiment && user?.id !== experiment.created_by) {
        setIsCreator(false);
        setAccessError(true);
      } else {
        setAccessError(false);
        setIsCreator(true);
      }
    };

    if (isOpen) {
      checkUser();
    }
  }, [isOpen, experiment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        alert('You must be logged in to create an experiment');
        return;
      }

      // Check if editing and not the creator
      if (experiment?.id && user.id !== experiment.created_by) {
        alert('You do not have permission to edit this experiment. Only the creator can edit it.');
        return;
      }

      const experimentData = {
        ...formData,
        project_id: projectId || experiment?.project_id,
        type: 'experiment',
        created_by: user.id,
        creator_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Unknown',
        creator_email: user.email,
      };

      if (experiment?.id) {
        // Update existing experiment
        const { error } = await supabase
          .from('experiments')
          .update(experimentData)
          .eq('id', experiment.id);

        if (error) throw error;
      } else {
        // Create new experiment
        const { error } = await supabase
          .from('experiments')
          .insert([experimentData]);

        if (error) throw error;
      }

      onSave();
      onClose();
      setFormData({
        title: '',
        category: 'App Development',
        status: 'Success',
        hypothesis: '',
        lesson_learned: '',
        method: '',
        expected_result: '',
        actual_result: '',
        linked_test: '',
      });
    } catch (error) {
      console.error('Error saving experiment:', error);
      alert('Error saving experiment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (accessError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
        <div className="bg-[#1a1f2e] rounded-lg shadow-xl w-full max-w-md m-4 border border-red-500/30 p-6">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">
            You do not have permission to edit this experiment. Only the creator can make changes.
          </p>
          <button
            onClick={onClose}
            className="w-full px-6 py-2 text-black bg-white rounded-md hover:bg-gray-200 transition-colors font-bold"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 pb-24 lg:pb-4">
      <div className="bg-black rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-hidden border border-white/10 flex flex-col">
        <div className="bg-black border-b border-white/10 px-4 lg:px-6 py-3 lg:py-4 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg lg:text-xl font-semibold text-white">
            {experiment ? 'Edit Experiment' : 'Log New Experiment'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* AI Quick-fill section */}
          <div className="p-4 lg:p-6 pb-0 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Describe your experiment
                <span className="ml-2 text-xs text-gray-500 font-normal">Dump everything here — hypothesis, method, results, lessons…</span>
              </label>
              <textarea
                value={dumpText}
                onChange={(e) => setDumpText(e.target.value)}
                placeholder="e.g. We tested whether adding a cache layer to the API would reduce latency. We ran the API under load with and without cache. Expected 40% improvement. Got 62% on reads. Cache invalidation was tricky on writes. Lesson: always test cache invalidation paths separately. Category: Backend."
                rows={7}
                className="w-full px-4 py-3 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder-gray-500 text-sm leading-relaxed"
              />
            </div>

            {/* Quick category + status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white"
                >
                  <option value="App Development">App Development</option>
                  <option value="Frontend">Frontend</option>
                  <option value="Backend">Backend</option>
                  <option value="Cloud">Cloud</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white"
                >
                  <option value="Success">Success</option>
                  <option value="Partial Success">Partial Success</option>
                  <option value="Failure">Failure</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Fill by AI button */}
            <button
              type="button"
              onClick={handleFillByAI}
              disabled={aiLoading || !dumpText.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-white text-black font-bold hover:bg-gray-200 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.05)]"
            >
              <Sparkles size={16} />
              Fill Form by AI
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 px-4 lg:px-6 py-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Form Details</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Loading overlay for AI */}
          <div className={`relative ${aiLoading ? '' : ''}`}>
            {aiLoading && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center gap-3 rounded-md mx-4 lg:mx-6">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-white font-medium">AI is filling the form…</p>
              </div>
            )}

            <div className="px-4 lg:px-6 space-y-4">
              {/* Experiment Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Experiment Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Testing API Latency with New Cache Layer"
                  className="w-full px-4 py-2 bg-black border border-white/20 rounded-md focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-gray-500"
                  required
                />
              </div>

              {/* Linked Test */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Linked Test (Optional)
                </label>
                <input
                  type="text"
                  name="linked_test"
                  value={formData.linked_test}
                  onChange={handleChange}
                  placeholder="e.g., Previous Test Case Name or ID"
                  className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white placeholder-gray-500"
                />
              </div>

              {/* Category and Status (synced with top selects) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white"
                    required
                  >
                    <option value="App Development">App Development</option>
                    <option value="Frontend">Frontend</option>
                    <option value="Backend">Backend</option>
                    <option value="Cloud">Cloud</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white"
                    required
                  >
                    <option value="Success">Success</option>
                    <option value="Partial Success">Partial Success</option>
                    <option value="Failure">Failure</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>

              {/* Hypothesis */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Hypothesis (What do you expect to happen?)
                </label>
                <textarea
                  name="hypothesis"
                  value={formData.hypothesis}
                  onChange={handleChange}
                  placeholder="If we implement X, then Y will happen because..."
                  rows="3"
                  className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder-gray-500"
                />
              </div>

              {/* Lesson Learned */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Lesson Learned (What did this failure/success teach us?)
                </label>
                <textarea
                  name="lesson_learned"
                  value={formData.lesson_learned}
                  onChange={handleChange}
                  placeholder="Key takeaway for future experiments..."
                  rows="3"
                  className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder-gray-500"
                />
              </div>

              {/* Method */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Method (How are you testing it?)
                </label>
                <textarea
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  placeholder="Step-by-step process..."
                  rows="3"
                  className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder-gray-500"
                />
              </div>

              {/* Expected Result and Actual Result */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Expected Result
                  </label>
                  <textarea
                    name="expected_result"
                    value={formData.expected_result}
                    onChange={handleChange}
                    placeholder="What is the predicted outcome?"
                    rows="3"
                    className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Actual Result
                  </label>
                  <textarea
                    name="actual_result"
                    value={formData.actual_result}
                    onChange={handleChange}
                    placeholder="What actually happened?"
                    rows="3"
                    className="w-full px-4 py-2 bg-[#0a0e1a] border border-white/20 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 lg:gap-4 p-4 lg:p-6 pt-4 mt-4 border-t border-white/10 bg-black flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 lg:px-6 py-2 rounded-md border border-white/20 text-gray-300 hover:bg-white/5 transition-colors text-sm lg:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 lg:px-6 py-2 rounded-md bg-white text-black hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm lg:text-base border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.05)]"
            >
              {loading ? 'Saving...' : experiment ? 'Update Experiment' : 'Save Experiment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExperimentFormModal;
